import { Button, message, Upload as UploadAntd, Image } from "antd";
import React, { useState, useEffect } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { ShowToast, Severty } from "../helper/toast";
import { uploadToS3 } from "../helper/s3";
import lang from "../helper/langHelper";

const MIME_TO_EXT = {
  "image/jpeg": "JPG",
  "image/jpg": "JPG",
  "image/png": "PNG",
  "image/gif": "GIF",
  "image/webp": "WebP",
};
const getAcceptedFormatsText = (types) => {
  if (!Array.isArray(types) || types.length === 0) return "JPG, PNG";
  const names = [...new Set(types.map((t) => MIME_TO_EXT[t] || t))].filter(Boolean);
  return names.length ? names.join(", ") : "JPG, PNG";
};

const SingleImageUpload = ({
  fileType = ["image/jpeg", "image/png", "image/jpg"],
  value,
  imageType = "images",
  btnName,
  onChange,
  size = 5,
  width,
  height,
  isDimension = false,
  acceptHint,
  ...props
}) => {
  const [file, setFile] = useState([]);
  const [uploading, setUploading] = useState(false);
  const acceptedFormats = getAcceptedFormatsText(fileType);

  const checkImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = document.createElement("img");

      img.onload = () => {
        if (width && height) {
          if (img.width === width && img.height === height) {
            resolve();
          } else {
            reject(
              `Please upload an image with dimensions (${width}X${height}). uploaded image is ${img.width} X ${img.height}`,
            );
          }
        } else {
          resolve();
        }
      };

      img.onerror = () => reject("Failed to load image");
      img.src = URL.createObjectURL(file);
    });
  };

  const beforeUpload = async (file) => {
    try {
      // Validate file type
      if (!fileType.includes(file.type)) {
        ShowToast(
          `Invalid format. Accepted: ${acceptedFormats}. Max size: ${size} MB.`,
          Severty.ERROR
        );
        return UploadAntd.LIST_IGNORE; // Prevent upload
      }
      
      // Validate file size
      const isLtSize = file.size / 1024 / 1024 < size;
      if (!isLtSize) {
        ShowToast(`Image must be smaller than ${size} MB!`, Severty.ERROR);
        return UploadAntd.LIST_IGNORE; // Prevent upload
      }
      
      // Check dimensions if required
      if (isDimension && width && height) {
        await checkImageDimensions(file);
      }
      
      // Return false to prevent default upload, but allow customRequest
      return false;
    } catch (err) {
      ShowToast(err.message || err, Severty.ERROR);
      return UploadAntd.LIST_IGNORE; // Prevent upload on error
    }
  };

  const handleImgChange = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;

    try {
      setUploading(true);

      if (onProgress) onProgress({ percent: 10 });

      const fileObj =
        file instanceof File
          ? file
          : file.originFileObj instanceof File
          ? file.originFileObj
          : file.originFileObj || null;

      if (!fileObj || !(fileObj instanceof File)) {
        throw new Error("No valid file object found");
      }

      setFile([{ ...file, status: "uploading", percent: 10 }]);

      if (onProgress) onProgress({ percent: 30 });

      const fileUrl = await uploadToS3(fileObj, undefined, ({ percent }) => {
        if (onProgress) onProgress({ percent: Math.max(30, percent) });
      });

      if (onProgress) onProgress({ percent: 100 });

      const fileData = {
        uid: file.uid || `-${Date.now()}`,
        name: file.name,
        status: "done",
        url: fileUrl,
        thumbUrl: fileUrl,
        response: { url: fileUrl },
      };

      setFile([fileData]);
      setUploading(false);

      if (onSuccess) onSuccess({ url: fileUrl }, fileData);
      if (onChange)  onChange(fileUrl);
    } catch (err) {
      setUploading(false);
      setFile([]);
      ShowToast(err?.message || "Failed to upload image", Severty.ERROR);
      if (onError)  onError(err);
      if (onChange) onChange("");
    }
  };

  const onRemove = () => {
    setFile([]);
    if (onChange) {
      onChange("");
    }
  };

  useEffect(() => {
    if (value) {
      if (typeof value === "string" && value.trim() !== "") {
        setFile([{
          uid: "-1",
          name: "image",
          status: "done",
          url: value,
          thumbUrl: value,
        }]);
      } else if (Array.isArray(value) && value.length > 0) {
        setFile(value);
      } else {
        setFile([]);
      }
    } else {
      setFile([]);
    }
  }, [value]);

  return (
    <div className="upload-area">
      {(acceptHint !== false) && (
        <div
          style={{
            fontSize: "12px",
            color: "#8c8c8c",
            marginBottom: "8px",
          }}
        >
          {acceptHint || `Accepted: ${acceptedFormats}. Max size: ${size} MB.`}
        </div>
      )}
      <UploadAntd
        listType="picture-card"
        maxCount={1}
        className="upload-post-inn"
        beforeUpload={beforeUpload}
        customRequest={handleImgChange}
        onChange={(info) => {
          // This onChange is called when file list changes
          console.log("Upload onChange triggered:", info);
          
          const { file, fileList } = info;
          
          // If file is being removed
          if (file.status === 'removed') {
            onRemove();
            return;
          }
          
          // If file is just selected (not yet uploading), trigger upload manually
          if (file.status === undefined || file.status === 'ready' || !file.status) {
            console.log("File selected, triggering upload manually");
            
            // Create a proper file object with uid if it doesn't have one
            const fileWithUid = {
              ...file,
              uid: file.uid || `-${Date.now()}`,
              originFileObj: file instanceof File ? file : file.originFileObj || file
            };
            
            // Manually trigger the upload
            handleImgChange({
              file: fileWithUid,
              onSuccess: (response, fileData) => {
                console.log("Manual upload success:", response);
              },
              onError: (error) => {
                console.error("Manual upload error:", error);
              },
              onProgress: (progress) => {
                console.log("Manual upload progress:", progress);
              }
            });
          }
        }}
        onRemove={onRemove}
        fileList={file}
        disabled={uploading}
        showUploadList={{
          showPreviewIcon: true,
          showRemoveIcon: true,
        }}
        {...props}
      >
        {file && file.length > 0 ? null : (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <div className="ant-upload-drag-icon" style={{ marginBottom: "8px" }}>
              <UploadOutlined style={{ fontSize: "32px", color: "#1890ff" }} />
            </div>
            <div className="ant-upload-text" style={{ color: "#666" }}>
              {btnName ? `${lang("Upload")} ${btnName}` : "Click to upload"}
            </div>
          </div>
        )}
      </UploadAntd>
    </div>
  );
};

export default SingleImageUpload;
